/**
 * The donor door, from the browser's side.
 *
 * Everything here talks to `/api/donor/*`, the only endpoints in the application that
 * answer without an identity, and only on the donor hostname. Nothing in this file
 * decides anything — the Worker does all of it — so these are thin on purpose.
 *
 * Note there is no `register`. There is no such endpoint to call.
 */
import { api } from './api'

/**
 * Is this browser at the donor door?
 *
 * Asked of the server rather than of `location.hostname`, because the hostname lives
 * in `wrangler.jsonc` and putting a copy of it in the bundle would be a second place
 * for it to be true. One 404 answers "no" for the staff app, which only asks after a
 * 401 has already ended the session — so this costs nothing on the path anybody is on.
 */
export async function donorContext() {
  try {
    return await api('donor/context')
  } catch {
    return null
  }
}

/*
 * `turnstileToken` is undefined when the challenge is switched off, and the Worker reads
 * a missing token as "not challenged" only in that case — `assertHuman()` returns early
 * on the same configuration the context endpoint used to decide not to send a site key.
 * So the two sides agree without this file having to know anything about it.
 */
export const donorSignIn = (email, password, turnstileToken) =>
  api('donor/session', { method: 'POST', body: { email, password, turnstileToken } })

export const donorForgot = (email, turnstileToken) =>
  api('donor/forgot', { method: 'POST', body: { email, turnstileToken } })

export const donorPeekToken = (token) =>
  api(`donor/token?token=${encodeURIComponent(token)}`)

export const donorSetPassword = (token, password) =>
  api('donor/password', { method: 'POST', body: { token, password } })

/**
 * End the session and go back to the sign-in page.
 *
 * A whole-page load rather than a re-render, for the same reason the staff sign-out is
 * a navigation: leaving the document takes every record the query cache had in memory
 * with it, which is most of the point of the button.
 */
export async function donorSignOut() {
  try {
    await api('donor/session', { method: 'DELETE' })
  } catch {
    // The cookie may already be gone or expired. Either way the next load asks again,
    // and there is nothing useful to say to somebody who is leaving.
  }
  window.location.replace('/')
}

/** Mirrors the Worker's rule, so the form can say so before asking the server. */
export const PASSWORD_MIN = 12
